import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Alert, AlertIcon, Badge, Box, Button, Flex, HStack, Input, Select, Spinner, Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';

// Superadmin property switch — same contract as the staff app's modal:
// newest hotels first, lead-oriented filters (trials, activity, country),
// and entering a property wipes the client and starts a fresh session as
// that hotel's admin (apiClient.superadminSwitchHotel).
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

const SuperadminHotelSwitchModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [switchingId, setSwitchingId] = useState(null);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('all');
  const [activity, setActivity] = useState('any');
  const [country, setCountry] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError('');
    apiClient.superadminHotels()
      .then((body) => { setRows(body.hotels || []); setCurrentId(body.current_hotel_id); })
      .catch(() => setError(t('hotelSwitch.errors.loadFailed')))
      .finally(() => setLoading(false));
  }, [isOpen, t]);

  const countries = useMemo(
    () => [...new Set(rows.map((r) => r.country).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (search) {
      const haystack = `${r.name} ${r.code} ${r.city || ''} ${r.email || ''}`.toLowerCase();
      if (!haystack.includes(search.toLowerCase())) return false;
    }
    if (kind === 'trial' && !r.is_trial) return false;
    if (kind === 'customer' && r.is_trial) return false;
    if (country && r.country !== country) return false;
    const idle = daysSince(r.last_active);
    if (activity === 'active7' && !(idle !== null && idle <= 7)) return false;
    if (activity === 'quiet30' && idle !== null && idle <= 30) return false;
    if (activity === 'never' && r.last_active) return false;
    return true;
  }), [rows, search, kind, activity, country]);

  const enterHotel = async (hotel) => {
    setSwitchingId(hotel.id);
    setError('');
    try {
      await apiClient.superadminSwitchHotel(hotel.id);
      window.location.href = '/';
    } catch (e) {
      setError(e.message || t('hotelSwitch.errors.switchFailed'));
      setSwitchingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '4xl' }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('hotelSwitch.title')}
          <Text fontSize="sm" color="gray.500" fontWeight="normal">
            {t('hotelSwitch.subtitle')}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {error && <Alert status="error" borderRadius="md" mb={3}><AlertIcon />{error}</Alert>}
          <Flex gap={2} mb={4} flexWrap="wrap">
            <Input placeholder={t('hotelSwitch.filters.searchPlaceholder')} value={search}
              onChange={(e) => setSearch(e.target.value)} size="sm" flex="2" minW="180px" autoFocus />
            <Select size="sm" flex="1" minW="130px" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="all">{t('hotelSwitch.filters.allHotels')}</option>
              <option value="trial">{t('hotelSwitch.filters.trials')}</option>
              <option value="customer">{t('hotelSwitch.filters.customers')}</option>
            </Select>
            <Select size="sm" flex="1" minW="140px" value={activity} onChange={(e) => setActivity(e.target.value)}>
              <option value="any">{t('hotelSwitch.filters.anyActivity')}</option>
              <option value="active7">{t('hotelSwitch.filters.active7')}</option>
              <option value="quiet30">{t('hotelSwitch.filters.quiet30')}</option>
              <option value="never">{t('hotelSwitch.filters.neverActive')}</option>
            </Select>
            <Select size="sm" flex="1" minW="120px" value={country}
              onChange={(e) => setCountry(e.target.value)} placeholder={t('hotelSwitch.filters.anyCountry')}>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Flex>

          {loading ? (
            <Box textAlign="center" py={10}><Spinner /></Box>
          ) : (
            <>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {t('hotelSwitch.list.count', { shown: filtered.length, count: rows.length })}
              </Text>
              {filtered.map((hotel) => {
                const idle = daysSince(hotel.last_active);
                const isCurrent = hotel.id === currentId;
                return (
                  <Flex key={hotel.id} align="center" gap={3} py={2} px={2}
                    borderBottomWidth="1px" borderColor="gray.100"
                    bg={isCurrent ? 'blue.50' : 'transparent'} borderRadius="md">
                    <Box flex="1" minW={0}>
                      <HStack spacing={2} flexWrap="wrap" rowGap={0}>
                        <Text fontWeight="semibold" noOfLines={1}>{hotel.name}</Text>
                        <Text fontSize="xs" color="gray.500">{hotel.code}</Text>
                        {hotel.is_trial && <Badge colorScheme="purple">{t('hotelSwitch.badges.trial')}</Badge>}
                        {!hotel.is_active && <Badge colorScheme="red">{t('hotelSwitch.badges.inactive')}</Badge>}
                        {!hotel.onboarding_completed && <Badge colorScheme="orange">{t('hotelSwitch.badges.onboarding')}</Badge>}
                        {isCurrent && <Badge colorScheme="blue">{t('hotelSwitch.badges.current')}</Badge>}
                      </HStack>
                      <Text fontSize="xs" color="gray.600" noOfLines={1}>
                        {[hotel.city, hotel.country].filter(Boolean).join(', ')}
                        {' · '}{t('hotelSwitch.list.roomsCount', { count: hotel.rooms })}
                        {' · '}{t('hotelSwitch.list.usersCount', { count: hotel.users })}
                        {hotel.created_at ? ` · ${t('hotelSwitch.list.created', { date: hotel.created_at.slice(0, 10) })}` : ''}
                        {' · '}{idle === null ? t('hotelSwitch.list.neverActive') : t('hotelSwitch.list.activeDaysAgo', { days: idle })}
                      </Text>
                    </Box>
                    <Button size="sm" colorScheme="blue" variant={isCurrent ? 'outline' : 'solid'}
                      isDisabled={isCurrent || switchingId !== null}
                      isLoading={switchingId === hotel.id}
                      onClick={() => enterHotel(hotel)}>
                      {t('hotelSwitch.buttons.enter')}
                    </Button>
                  </Flex>
                );
              })}
              {!filtered.length && (
                <Text color="gray.500" textAlign="center" py={8}>{t('hotelSwitch.list.empty')}</Text>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SuperadminHotelSwitchModal;
